import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { fetcher } from 'src/api/fetcher';
import { API_RESOURCES, MATERIAL_CONTAINER_STATUSES } from 'src/utils/constants';

const ALLOWED_STATUSES = [MATERIAL_CONTAINER_STATUSES.NEW, MATERIAL_CONTAINER_STATUSES.IN_USE];

/**
 * Builds base query parameters for material container filtering
 * @param {string} lotUri - The lot URI to filter by
 * @returns {Object} Base query parameters
 */
const buildBaseParams = lotUri => ({
  'filter[material_lot]': lotUri,
  'filter[status]': ALLOWED_STATUSES.join(','),
  sort: '-quantity',
});

/**
 * Fetches containers by their specific URIs
 * @param {string[]} uris - Array of container URIs to fetch
 * @param {number} limit - Maximum number of containers to fetch
 * @param {string} lotUri - The lot URI to filter by
 * @returns {Promise<{resources: Array, meta: Object}>}
 */
const fetchContainersByUris = async (uris, limit, lotUri) => {
  if (!uris?.length) {
    return { resources: [], meta: { count: 0 } };
  }

  return fetcher(API_RESOURCES.MATERIAL_CONTAINER, {
    params: {
      ...buildBaseParams(lotUri),
      'filter[uri]': uris.join(','),
      'page[limit]': limit,
    },
  });
};

/**
 * Fetches containers excluding specific URIs
 * @param {string} lotUri - The lot URI to filter by
 * @param {string[]} excludeUris - URIs to exclude from results
 * @param {number} limit - Maximum number of containers to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<{resources: Array, meta: Object}>}
 */
const fetchContainersExcludingUris = async (lotUri, excludeUris, limit, offset) => {
  const params = {
    ...buildBaseParams(lotUri),
    'page[limit]': limit,
    'page[offset]': offset,
  };

  if (excludeUris?.length) {
    params['filter[uri]!'] = excludeUris.join(',');
  }

  return fetcher(API_RESOURCES.MATERIAL_CONTAINER, { params });
};

/**
 * Fetches total count of containers for a lot
 * @param {string} lotUri - The lot URI to filter by
 * @returns {Promise<{resources: Array, meta: Object}>}
 */
const fetchContainersCount = async lotUri => {
  return fetcher(API_RESOURCES.MATERIAL_CONTAINER, {
    params: {
      ...buildBaseParams(lotUri),
      'page[limit]': 1,
    },
  });
};

/**
 * Fetches a page of containers with priority for specific URIs
 * Strategy:
 * 1. First, fetch containers with pre-selected amounts (priority containers)
 * 2. If priority containers don't fill the page, backfill with other containers
 * 3. Once all priority containers are fetched, fetch remaining containers
 * 4. Track actual fetched counts to handle multi-lot scenarios correctly
 *
 * @param {Object} params - Fetch parameters
 * @param {string[]} params.priorityUris - URIs that should be fetched first
 * @param {string[]} params.excludeUris - URIs to exclude from results
 * @param {string} params.lotUri - The lot URI to filter by
 * @param {number} params.pageSize - Number of items per page
 * @param {number} params.priorityFetchedCount - Number of priority containers actually fetched so far
 * @param {number} params.regularFetchedCount - Number of regular containers actually fetched so far
 * @returns {Promise<{resources: Array, meta: Object}>}
 */
const fetchPaginatedWithPriority = async ({
  priorityUris,
  excludeUris,
  lotUri,
  pageSize,
  priorityFetchedCount = 0,
  regularFetchedCount = 0,
}) => {
  const totalPriorityAvailable = priorityUris.length;
  const priorityRemaining = Math.max(0, totalPriorityAvailable - priorityFetchedCount);
  const shouldFetchPriority = priorityRemaining > 0;

  // CASE 1: Still fetching priority containers
  if (shouldFetchPriority) {
    const priorityToFetch = Math.min(pageSize, priorityRemaining);
    const urisToFetch = priorityUris.slice(
      priorityFetchedCount,
      priorityFetchedCount + priorityToFetch
    );

    const priorityData = await fetchContainersByUris(urisToFetch, priorityToFetch, lotUri);
    const actualPriorityFetched = priorityData.resources.length;

    // If priority containers don't fill the page, backfill with regular containers
    const needsBackfill = actualPriorityFetched < pageSize;

    if (needsBackfill) {
      const backfillCount = pageSize - actualPriorityFetched;
      const backfillData = await fetchContainersExcludingUris(
        lotUri,
        excludeUris,
        backfillCount,
        regularFetchedCount // Use actual count of regular containers fetched so far
      );

      return {
        resources: [...priorityData.resources, ...backfillData.resources],
        meta: {
          priorityFetchedCount: priorityFetchedCount + actualPriorityFetched,
          regularFetchedCount: regularFetchedCount + backfillData.resources.length,
        },
      };
    }

    // Priority containers filled the page
    return {
      resources: priorityData.resources,
      meta: {
        priorityFetchedCount: priorityFetchedCount + actualPriorityFetched,
        regularFetchedCount,
      },
    };
  }

  // CASE 2: All priority containers fetched, now fetch remaining regular containers
  const regularData = await fetchContainersExcludingUris(
    lotUri,
    excludeUris,
    pageSize,
    regularFetchedCount // Use actual count of regular containers fetched so far
  );

  return {
    resources: regularData.resources,
    meta: {
      priorityFetchedCount,
      regularFetchedCount: regularFetchedCount + regularData.resources.length,
    },
  };
};

/**
 * Infinite scroll hook for containers in SCAN mode
 *
 * Features:
 * - Shows scanned container first (pinned at top)
 * - Prioritizes containers with pre-selected amounts
 * - Supports infinite scrolling with efficient pagination
 * - Manual fetch only (no auto-refetch)
 *
 * @param {string} lotUri - The lot URI to filter containers
 * @param {string} scannedContainerUri - URI of the currently scanned container
 * @param {string[]} containerUrisWithAmounts - URIs of containers with pre-selected amounts
 * @param {boolean} enabled - Whether the query is enabled
 * @param {number} initialLimit - Number of items to fetch per page
 * @param {string} key - Unique key for the query cache
 * @returns {Object} Query result with containers data and pagination controls
 */
export const useContainersPaginatedScanMode = (
  lotUri,
  scannedContainerUri,
  containerUrisWithAmounts = [],
  enabled = true,
  initialLimit = 25,
  key = 'containers-scan-mode'
) => {
  const queryClient = useQueryClient();

  // Remove scanned container from priority list (it's shown separately)
  const priorityContainerUris = useMemo(
    () => containerUrisWithAmounts.filter(uri => uri !== scannedContainerUri),
    [containerUrisWithAmounts, scannedContainerUri]
  );

  const queryKey = useMemo(
    () => ['containers-scan', key, lotUri, scannedContainerUri, priorityContainerUris.length],
    [key, lotUri, scannedContainerUri, priorityContainerUris.length]
  );

  // Cleanup queries when disabled
  useEffect(() => {
    if (!enabled && lotUri) {
      queryClient.removeQueries({ queryKey, exact: true });
    }
  }, [enabled, lotUri, queryClient, queryKey]);

  // Fetch the scanned container (shown at top)
  const { data: scannedContainerData } = useQuery({
    queryKey: ['container', 'scanned', scannedContainerUri, lotUri],
    queryFn: () => fetchContainersByUris([scannedContainerUri], 1, lotUri),
    enabled: enabled && !!scannedContainerUri && !!lotUri,
  });

  // Fetch total count for pagination
  const { data: totalCountData } = useQuery({
    queryKey: ['container', 'total-count', lotUri],
    queryFn: () => fetchContainersCount(lotUri),
    enabled: enabled && !!lotUri,
  });

  // Infinite query for paginated containers (excluding scanned)
  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      const { priorityFetchedCount = 0, regularFetchedCount = 0 } = pageParam || {};

      return fetchPaginatedWithPriority({
        priorityUris: priorityContainerUris,
        excludeUris: [...priorityContainerUris, scannedContainerUri],
        lotUri,
        pageSize: initialLimit,
        priorityFetchedCount,
        regularFetchedCount,
      });
    },
    enabled: false, // Manual fetch only
    initialPageParam: { priorityFetchedCount: 0, regularFetchedCount: 0 },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    getNextPageParam: (lastPage, pages) => {
      const totalFetched = pages.reduce((sum, page) => sum + page.resources.length, 0);
      const totalCount = totalCountData?.meta?.count || 0;
      const totalAvailable = totalCount - 1; // -1 for scanned container (shown separately)

      if (totalFetched >= totalAvailable) {
        return undefined;
      }

      // Return the cumulative counts from the last page's meta
      return {
        priorityFetchedCount: lastPage.meta.priorityFetchedCount,
        regularFetchedCount: lastPage.meta.regularFetchedCount,
      };
    },
  });

  // Combine scanned container with paginated results
  const data = useMemo(() => {
    const scannedContainer = scannedContainerData?.resources?.[0];
    const totalCount = totalCountData?.meta?.count || 0;

    // If no paginated data yet, show only scanned container
    if (!infiniteQuery.data) {
      return {
        containers: scannedContainer ? [scannedContainer] : [],
        totalCount,
        hasMore: totalCount > 1,
      };
    }

    // Combine scanned container with paginated results
    const paginatedContainers = infiniteQuery.data.pages.flatMap(page => page.resources);
    const allContainers = scannedContainer
      ? [scannedContainer, ...paginatedContainers]
      : paginatedContainers;

    return {
      containers: allContainers,
      totalCount,
      hasMore: allContainers.length < totalCount,
    };
  }, [scannedContainerData, totalCountData, infiniteQuery.data]);

  const hasNextPage = useMemo(() => {
    const totalCount = totalCountData?.meta?.count || 0;
    return (data?.containers?.length || 0) < totalCount;
  }, [totalCountData, data]);

  const isLoading = (!scannedContainerData || !totalCountData) && infiniteQuery.isLoading;

  return {
    ...infiniteQuery,
    data,
    hasNextPage,
    isLoading,
  };
};

/**
 * Infinite scroll hook for containers in EDIT mode
 *
 * Features:
 * - Prioritizes containers with pre-selected amounts (shown first)
 * - Supports infinite scrolling with efficient pagination
 * - Auto-fetches on mount when enabled
 *
 * @param {string} lotUri - The lot URI to filter containers
 * @param {string[]} containerUrisWithAmounts - URIs of containers with pre-selected amounts
 * @param {boolean} enabled - Whether the query is enabled
 * @param {number} initialLimit - Number of items to fetch per page
 * @param {string} key - Unique key for the query cache
 * @returns {Object} Query result with containers data and pagination controls
 */
export const useContainersPaginatedEditMode = (
  lotUri,
  containerUrisWithAmounts = [],
  enabled = true,
  initialLimit = 25,
  key = 'containers-edit-mode'
) => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['containers-edit', key, lotUri, containerUrisWithAmounts.length],
    [key, lotUri, containerUrisWithAmounts.length]
  );

  // Cleanup queries when disabled
  useEffect(() => {
    if (!enabled && lotUri) {
      queryClient.removeQueries({ queryKey, exact: true });
    }
  }, [enabled, lotUri, queryClient, queryKey]);

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const isFirstPage = !pageParam;
      const { priorityFetchedCount = 0, regularFetchedCount = 0 } = pageParam || {};

      const totalPriorityUris = containerUrisWithAmounts.length;
      const priorityRemaining = Math.max(0, totalPriorityUris - priorityFetchedCount);
      const shouldFetchPriority = priorityRemaining > 0;

      // CASE 1: Still fetching priority containers (with amounts)
      if (shouldFetchPriority) {
        const priorityToFetch = Math.min(initialLimit, priorityRemaining);
        const urisToFetch = containerUrisWithAmounts.slice(
          priorityFetchedCount,
          priorityFetchedCount + priorityToFetch
        );

        const priorityData = await fetchContainersByUris(urisToFetch, priorityToFetch, lotUri);
        const actualPriorityFetched = priorityData.resources.length;

        // For Edit Mode: DON'T backfill on the FIRST page
        // Show only containers with amounts initially
        const needsBackfill = actualPriorityFetched < initialLimit;
        const shouldBackfill = needsBackfill && !isFirstPage;

        if (shouldBackfill) {
          const backfillCount = initialLimit - actualPriorityFetched;
          const backfillData = await fetchContainersExcludingUris(
            lotUri,
            containerUrisWithAmounts,
            backfillCount,
            regularFetchedCount
          );

          return {
            resources: [...priorityData.resources, ...backfillData.resources],
            meta: {
              priorityFetchedCount: priorityFetchedCount + priorityToFetch,
              regularFetchedCount: regularFetchedCount + backfillData.resources.length,
              actualPriorityCount: actualPriorityFetched,
            },
          };
        }

        // Get total count for pagination (only on first page)
        let totalCount = 0;
        if (isFirstPage) {
          const countData = await fetchContainersCount(lotUri);
          totalCount = countData.meta.count;
        }

        return {
          resources: priorityData.resources,
          meta: {
            priorityFetchedCount: priorityFetchedCount + priorityToFetch,
            regularFetchedCount,
            actualPriorityCount: actualPriorityFetched,
            ...(isFirstPage && { totalCount }),
          },
        };
      }

      // CASE 2: All priority containers fetched, now fetch remaining containers
      const regularData = await fetchContainersExcludingUris(
        lotUri,
        containerUrisWithAmounts,
        initialLimit,
        regularFetchedCount
      );

      // Get total count for pagination (only on first page)
      let totalCount;
      if (isFirstPage) {
        const countData = await fetchContainersCount(lotUri);
        totalCount = countData.meta.count;
      }

      return {
        resources: regularData.resources,
        meta: {
          priorityFetchedCount,
          regularFetchedCount: regularFetchedCount + regularData.resources.length,
          actualPriorityCount: 0,
          ...(isFirstPage && { totalCount }),
        },
      };
    },
    enabled: enabled && !!lotUri,
    initialPageParam: null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    getNextPageParam: (lastPage, pages) => {
      const totalFetched = pages.reduce((sum, page) => sum + page.resources.length, 0);

      // Get total count from first page's meta
      const firstPage = pages[0];
      const totalCount = firstPage?.meta?.totalCount;

      if (!totalCount || totalFetched >= totalCount) {
        return undefined;
      }

      // Return the cumulative counts from the last page's meta
      return {
        priorityFetchedCount: lastPage.meta.priorityFetchedCount,
        regularFetchedCount: lastPage.meta.regularFetchedCount,
      };
    },
    select: data => {
      const allContainers = data.pages.flatMap(page => page.resources);
      const firstPage = data.pages[0];
      const totalCount = firstPage?.meta?.totalCount || 0;

      return {
        containers: allContainers,
        totalCount,
        hasMore: allContainers.length < totalCount,
      };
    },
  });
};
